/**
 * THE TILL SERVICE. One place that knows what a till is and what it maps to.
 *
 * A VASP till is a payment handle: `LPUSDTTRC204K7Q2M`, printed on a counter, sent in a
 * message, pasted into Send Money by somebody who has never seen the recipient's wallet.
 * Conceptually it is the Lipa na M-PESA till of Kenyan retail, pointed at a crypto receiving wallet
 * instead of a paybill.
 *
 * ## The format carries the mapping
 *
 *   LP  USDT  TRC20  4K7Q2M
 *   │   │     │      └── the code: 6 characters, the only random part
 *   │   │     └── the network, as its token standard. TRC20, ERC20, BEP20, BTC
 *   │   └── the asset
 *   └── the platform
 *
 * There are no separators, because a till is typed on a phone keypad at a counter, and a Lipa na
 * M-PESA till has none either. The segments are separated by TYPOGRAPHY where they are displayed,
 * never by characters in the value. Input is normalised, so anybody who types the hyphens anyway
 * still reaches the right till.
 *
 * A bare `LP4K7Q2M` was the first design and it was wrong. The two facts that decide whether a
 * payment arrives are WHICH ASSET and WHICH CHAIN, and a handle carrying neither has to be explained
 * out of band every time it is used. USDT sent on the wrong chain to an address that looks correct is
 * the most common way people lose stablecoin, and it is unrecoverable. Here the payer reads both off
 * the handle before they pay, the app checks them at resolution, and a mismatch is visible rather
 * than silent.
 *
 * Ambiguous characters are excluded from the code (no O, 0, I or 1), because this gets dictated.
 *
 * ## Why this is a service and not a hook
 *
 * The mapping from a till to a wallet is the whole security property. If a component resolved a till
 * by reaching into a list of the user's own tills, then a till belonging to somebody ELSE would not
 * resolve at all, and the natural next step would be to let the payer type a wallet address instead
 * -- which is exactly the substitution a till exists to prevent. So resolution is a call to the
 * service, always, and the caller pays whoever the SERVICE names.
 *
 * The security rules, which the API shape enforces rather than documenting:
 *
 *   1. A till never carries key material, a seed, a custody handle or an internal wallet id. Only the
 *      receiving address, which is public by construction.
 *   2. Resolving somebody's till returns a display name and a destination, never their account.
 *   3. Nothing here is a control on its own. The backend authorises the payment; this decides what
 *      the payer is SHOWN before they commit.
 *
 * ## Today it runs on fixtures
 *
 * There is no `/tills` service in `backend/src/lipa/services` yet, so these calls land in the UI mock
 * layer (`lib/mocks.ts`) exactly as every other Phase-2 super-app surface does. The seam is the API
 * client, not this file: when a real service ships, nothing in here or above it changes.
 */

import { api, LipaApiError, type Till, type TillOptions, type TillResolution } from './api';

/**
 * `LPUSDTTRC204K7Q2M`. Prefix, asset, network, code, and NO SEPARATORS.
 *
 * The hyphens went because a till gets typed, and typed most often on a phone keypad by somebody
 * standing at a counter. A Lipa na M-PESA till is digits with nothing in between for the same reason.
 * The parts are still there and are still read at a glance, but the separation is TYPOGRAPHY, not
 * characters: the card and the poster tint and space the segments, and the value itself stays one
 * unbroken token that copies, scans and dictates cleanly.
 *
 * The pattern is a SHAPE test, not a grammar. Where the segments begin and end is decided by the
 * record the service returns, never by guessing at a string, which is why nothing here needs the
 * asset catalogue to read a handle.
 */
export const TILL_PATTERN = /^LP[A-Z0-9]{10,24}$/;

/** No O, 0, I or 1: a till gets read down a phone line and dictated across a counter. */
export const TILL_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** The random tail is always six characters. Everything before it is the asset and the network. */
export const TILL_CODE_LENGTH = 6;

/**
 * Does this look like somebody trying to type a till?
 *
 * Deliberately looser than `TILL_PATTERN`: it matches the SHAPE, so a half-typed `LPUSDTTR` is still
 * treated as a till attempt and simply waits, rather than being treated as a wallet address and sent
 * to a stranger.
 */
export function looksLikeTill(input: string): boolean {
  return /^\s*lp[a-z0-9\s-]*$/i.test(input) && /^\s*lp/i.test(input);
}

/**
 * Upper-cased, with every separator removed.
 *
 * Hyphens and spaces are STRIPPED rather than rejected. People add them when reading a long code
 * aloud, some will have an old hyphenated handle written down, and "LP-USDT-TRC20-4K7Q2M" means
 * exactly the same till as the value we store. Refusing it would be pedantry at the till.
 */
export function normalizeTillNumber(input: string): string {
  return (input ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** The six-character tail, which is the only part that identifies one till from another. */
export function tillCode(input: string): string {
  return normalizeTillNumber(input).slice(-TILL_CODE_LENGTH);
}

/**
 * Split a handle into its parts, using the RECORD as the authority.
 *
 * The segments cannot be recovered from the string alone: `LPUSDCERC20...` and a future
 * `LPUSD-CERC20...` are indistinguishable without knowing which assets exist, and a client that
 * guessed would eventually guess wrong about somebody's money. So the record says what the asset and
 * the network are, and this checks the handle agrees with it. Null means it does not, which is a
 * refusal, not a display problem.
 */
export function tillSegments(
  till: Pick<Till, 'till_number' | 'asset' | 'network_tag'>,
): { asset: string; networkTag: string; code: string } | null {
  const canonical = normalizeTillNumber(till.till_number);
  const expected = `LP${till.asset}${till.network_tag}`;
  if (!canonical.startsWith(expected)) return null;
  const code = canonical.slice(expected.length);
  if (code.length !== TILL_CODE_LENGTH) return null;
  return { asset: till.asset, networkTag: till.network_tag, code };
}

/** Does a typed handle agree with the asset and chain the service resolved it to? */
export function tillMatchesRecord(handle: string, asset: string, networkTag: string): boolean {
  return tillSegments({ till_number: handle, asset, network_tag: networkTag }) !== null;
}

/**
 * The message a participant sends somebody.
 *
 * It names the asset and the chain in words as well as in the handle. The handle is precise and the
 * sentence is unmissable, and somebody about to send USDT on the wrong chain needs both.
 */
export function tillShareText(
  till: Pick<Till, 'till_number' | 'asset' | 'network' | 'network_tag'>,
): string {
  return (
    `Pay me using my VASP ${till.asset} Till:\n\n${till.till_number}\n\n` +
    `${till.asset} on ${till.network} (${till.network_tag}) only.`
  );
}

export class TillNotFoundError extends Error {
  constructor(readonly tillNumber: string) {
    super(`No VASP till matches ${tillNumber}.`);
    this.name = 'TillNotFoundError';
  }
}

export const tillService = {
  /** Which assets and chains a till may be issued for. Served, never assumed. */
  async options(): Promise<TillOptions> {
    const options = await api.tillOptions();
    return {
      assets: options?.assets ?? [],
      custody_is_simulated: options?.custody_is_simulated ?? true,
    };
  },

  /** Every till this participant holds, active first. */
  async listUserTills(): Promise<Till[]> {
    const { tills } = await api.tills();
    return tills ?? [];
  },

  /**
   * Issue a till, or hand back the one that already exists for this asset AND chain.
   *
   * ONE ACTIVE TILL PER PARTICIPANT PER ASSET PER NETWORK. A participant who presses the button
   * twice on the same pair has not asked for two payment handles; they have asked where their
   * payment handle is, and two handles for one destination is a shopkeeper printing one number and
   * watching payments arrive against another. A different chain is a genuinely different destination,
   * so it gets its own till rather than overloading one.
   */
  async generateTill(asset: string, networkTag: string): Promise<Till> {
    return api.createTill(asset, networkTag);
  },

  /** One till of the caller's own, by number. Null when they do not have it. */
  async getTill(tillNumber: string): Promise<Till | null> {
    const canonical = normalizeTillNumber(tillNumber);
    const tills = await this.listUserTills();
    return tills.find((t) => t.till_number === canonical) ?? null;
  },

  /**
   * ANYBODY's till, for a payer about to send to it.
   *
   * Throws `TillNotFoundError` on an unknown or retired handle, because "we could not find that till"
   * and "we could not reach the server" are different sentences and the payer acts differently on
   * each: one is a typo they can fix, the other is a wait.
   */
  async resolveTill(tillNumber: string): Promise<TillResolution> {
    const canonical = normalizeTillNumber(tillNumber);
    if (!TILL_PATTERN.test(canonical)) throw new TillNotFoundError(canonical);

    try {
      const resolved = await api.resolveTill(canonical);
      // A fixture that returns an empty object on an unmatched route would otherwise sail through as
      // a "resolved" till with an undefined recipient, and the payer would be shown a recipient of
      // "undefined" and allowed to continue.
      if (!resolved?.recipient_id || resolved.status !== 'active') {
        throw new TillNotFoundError(canonical);
      }

      /**
       * THE HANDLE AND THE RECORD MUST AGREE.
       *
       * The till number states its asset and chain, and so does the record the service returned. If
       * they differ, something is wrong that the payer cannot see -- a stale handle, a re-issued
       * code, a service and a client disagreeing about a chain -- and the safe reading of an
       * inconsistent payment destination is that it is not a destination at all.
       */
      if (!tillMatchesRecord(canonical, resolved.asset, resolved.network_tag)) {
        throw new TillNotFoundError(canonical);
      }

      return resolved;
    } catch (err) {
      if (err instanceof TillNotFoundError) throw err;
      if (err instanceof LipaApiError && err.detail.code === 'NOT_FOUND') {
        throw new TillNotFoundError(canonical);
      }
      throw err;
    }
  },

  /** Retire a till. Past payments are untouched; the handle simply stops resolving. */
  async deactivateTill(tillNumber: string): Promise<Till> {
    return api.deactivateTill(normalizeTillNumber(tillNumber));
  },
};
